import React, { useState } from 'react';
import { DERIVED_RATES } from '../constants/pricing';

const ArbitrageCalculatorModal = ({ isOpen, onClose }) => {
  const [icpAmount, setIcpAmount] = useState(100);

  if (!isOpen) return null;

  // Real-time market calculation logic (consistent with other components)
  const calculateStrategies = (amount) => {
    // Use centralized pricing constants
    const marketRate = DERIVED_RATES.icpToNICP_Market; // nICP per ICP
    const waterNeuronRate = marketRate; // Assume same as market rate
    const dexRate = marketRate; // Same market rate
    
    const waterNeuronReceived = amount * waterNeuronRate;
    const dexReceived = amount * dexRate;
    
    const waterNeuronProjection = amount; // 1:1 after 6 months
    const dexProjection = amount * 1.0533; // Higher due to better entry rate
    
    const waterNeuronApy = 0; // Due to lower rate
    const dexApy = 10.66; // Better effective APY

    return {
      waterNeuron: {
        received: waterNeuronReceived,
        rate: waterNeuronRate,
        airdrop: 50,
        projection: waterNeuronProjection,
        apy: waterNeuronApy
      },
      dex: {
        received: dexReceived,
        rate: dexRate,
        airdrop: 0,
        projection: dexProjection,
        apy: dexApy
      }
    };
  };

  const strategies = calculateStrategies(icpAmount);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="modal-overlay" onClick={handleOverlayClick}>
      <div className="modal-content arbitrage-modal">
        <div className="modal-header">
          <div className="modal-title-section">
            <div className="modal-icon">🏛️</div>
            <div>
              <h2 className="modal-title">nICP Arbitrage Calculator</h2>
              <p className="modal-subtitle">Compare direct WaterNeuron staking vs DEX purchase strategies</p>
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Input Section */}
          <div className="input-section">
            <label className="input-label">ICP Amount to Invest</label>
            <div className="input-container">
              <input
                type="number"
                value={icpAmount}
                onChange={(e) => setIcpAmount(Number(e.target.value))}
                className="amount-input"
              />
              <span className="input-suffix">ICP</span>
            </div>
          </div>

          {/* Comparison Cards */}
          <div className="comparison-grid">
            {/* WaterNeuron Strategy */}
            <div className="strategy-card">
              <div className="strategy-header">
                <div className="strategy-icon water">💧</div>
                <h3 className="strategy-name">Direct WaterNeuron</h3>
              </div>
              
              <div className="strategy-details">
                <div className="detail-row">
                  <span className="detail-label">You receive:</span>
                  <span className="detail-value primary">{strategies.waterNeuron.received.toFixed(6)} nICP</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Exchange rate:</span>
                  <span className="detail-value">1 ICP = {strategies.waterNeuron.rate} nICP</span>
                </div>
                
                <div className="detail-row airdrop">
                  <span className="detail-label">🌿 WTN Airdrop:</span>
                  <span className="detail-value success">{strategies.waterNeuron.airdrop} tokens</span>
                </div>
              </div>

              <div className="projection-section">
                <h4 className="projection-title">6-Month Unstaking Projection:</h4>
                <div className="projection-details">
                  <div className="projection-row">
                    <span>You'll receive:</span>
                    <span className="projection-value">{strategies.waterNeuron.projection.toFixed(2)} ICP</span>
                  </div>
                  <div className="projection-row">
                    <span>Effective APY:</span>
                    <span className="projection-value neutral">{strategies.waterNeuron.apy.toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* DEX Strategy */}
            <div className="strategy-card">
              <div className="strategy-header">
                <div className="strategy-icon dex">🔄</div>
                <h3 className="strategy-name">DEX Purchase</h3>
              </div>
              
              <div className="strategy-details">
                <div className="detail-row">
                  <span className="detail-label">You receive:</span>
                  <span className="detail-value primary">{strategies.dex.received.toFixed(6)} nICP</span>
                </div>
                
                <div className="detail-row">
                  <span className="detail-label">Exchange rate:</span>
                  <span className="detail-value">1 ICP = {strategies.dex.rate} nICP</span>
                </div>
                
                <div className="detail-row no-airdrop">
                  <span className="detail-label">⚠️ No Airdrop:</span>
                  <span className="detail-value muted">0 tokens</span>
                </div>
              </div>

              <div className="projection-section">
                <h4 className="projection-title">6-Month Unstaking Projection:</h4>
                <div className="projection-details">
                  <div className="projection-row">
                    <span>You'll receive:</span>
                    <span className="projection-value">{strategies.dex.projection.toFixed(2)} ICP</span>
                  </div>
                  <div className="projection-row">
                    <span>Effective APY:</span>
                    <span className="projection-value positive">{strategies.dex.apy.toFixed(2)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Key Insights */}
          <div className="insights-section">
            <div className="insights-header">
              <span className="insights-icon">💡</span>
              <h3 className="insights-title">Key Insights</h3>
            </div>
            <ul className="insights-list">
              <li>• <strong>nICP grows over time</strong> as it represents staked neurons earning rewards</li>
              <li>• <strong>DEX route offers higher APY</strong> due to better entry rate (more nICP per ICP)</li>
              <li>• <strong>WTN direct route includes airdrop</strong> which may offset lower APY</li>
              <li>• <strong>Both require 6-month unstaking period</strong> with locked funds</li>
              <li>• <strong>DEX prices fluctuate</strong> - this is just an example calculation</li>
            </ul>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="modal-actions">
          <button className="action-button primary">
            <span className="button-icon">💧</span>
            Stake via WaterNeuron
          </button>
          <button className="action-button secondary">
            <span className="button-icon">🔄</span>
            Swap to nICP
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArbitrageCalculatorModal;
